import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, Clock, Loader2 } from "lucide-react";
import Button from "../../common/Button";
import NoDataFound from "../../common/NoDataFound";
import { useFormatDate } from "../../../contexts/EntitySettingsContext";
import {
    sopVersioningService,
    type SopChange,
    type SopVersionDetail,
    type SopVersionRow,
} from "../../../services/governanceService";
import { extractApiError } from "../../../utils/apiError";

// Plain-word labels for the fields a version change can touch.
const FIELD_LABEL: Record<string, string> = {
    process_name: "SOP name",
    description: "Description",
    owner: "Owner",
    owner_authority_id: "Owner",
    effective_date: "Effective date",
    review_cycle_months: "Review cycle (months)",
    step_number: "Position",
    step_name: "Name",
    step_type: "Type",
    timeline_days: "Days",
    timeline_anchor: "Counted from",
    after_step_name: "Follows step",
    is_active: "Active",
    authorities: "Who does it",
};

const CHANGE_BADGE: Record<SopChange["change"], string> = {
    added: "success",
    edited: "warning",
    removed: "danger",
};

const show = (v: any): string => {
    if (v === null || v === undefined || v === "") return "—";
    if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
    if (typeof v === "number" && (v === 0 || v === 1)) return String(v);
    return String(v);
};

/** What changed between two versions (or between the draft and the live SOP). */
export const SopChangesList: React.FC<{ changes: SopChange[] | null; emptyText?: string }> = ({ changes, emptyText }) => {
    if (!changes || changes.length === 0) {
        return <p className="text-xs text-gray-400 italic">{emptyText || "No changes."}</p>;
    }
    return (
        <ul className="flex flex-col gap-2">
            {changes.map((c, i) => (
                <li key={i} className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
                    <div className="flex items-center gap-2">
                        <span className={`status-badge sm ${CHANGE_BADGE[c.change]}`}>
                            {c.kind === "header" ? "SOP details" : "Step"} {c.change}
                        </span>
                        <span className="text-sm text-gray-800">{c.label}</span>
                    </div>
                    {c.fields.length > 0 && (
                        <table className="mt-2 w-full text-xs">
                            <tbody>
                                {c.fields.filter((f) => f.field !== "owner_authority_id").map((f, j) => (
                                    <tr key={j} className="align-top">
                                        <td className="py-0.5 pr-3 text-gray-500 whitespace-nowrap">{FIELD_LABEL[f.field] || f.field}</td>
                                        <td className="py-0.5 pr-3 text-red-700 line-through">{show(f.before)}</td>
                                        <td className="py-0.5 text-emerald-700">{show(f.after)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </li>
            ))}
        </ul>
    );
};

interface Props {
    processId: number;
    /** Bump to reload (e.g. after a new version is rolled out). */
    refreshKey?: number;
}

/** Versions tab — every version of the SOP that has been live, newest first.
    Click a version to read its frozen content and what changed from the one before. */
const SopVersionsPanel: React.FC<Props> = ({ processId, refreshKey }) => {
    const fmtDate = useFormatDate();
    const [rows, setRows] = useState<SopVersionRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [detail, setDetail] = useState<SopVersionDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setRows(await sopVersioningService.listVersions(processId));
        } catch (e: any) {
            setError(extractApiError(e, "Failed to load versions."));
        } finally {
            setLoading(false);
        }
    }, [processId]);

    useEffect(() => { setDetail(null); load(); }, [load, refreshKey]);

    const openVersion = async (n: number) => {
        setDetailLoading(true);
        try {
            setDetail(await sopVersioningService.getVersion(processId, n));
        } catch (e: any) {
            setError(extractApiError(e, "Failed to load this version."));
        } finally {
            setDetailLoading(false);
        }
    };

    if (loading || detailLoading) {
        return (
            <div className="p-6 flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin mr-2" size={16} /> Loading versions…
            </div>
        );
    }
    if (error) return <div className="p-3 text-sm text-red-600">{error}</div>;

    if (detail) {
        const h = detail.snapshot.header;
        return (
            <div className="flex flex-col gap-4" data-testid="PNL_sop_version_detail">
                <div className="flex items-center gap-2">
                    <Button variant="outline" icon={ChevronLeft} onClick={() => setDetail(null)} data-testid="BTN_sop_version_back">
                        All versions
                    </Button>
                    <span className="text-sm font-semibold text-gray-800">Version {detail.version_number}</span>
                    <span className={`status-badge sm ${detail.is_live ? "success" : "neutral"}`}>
                        {detail.is_live ? "Live" : "Superseded"}
                    </span>
                    <span className="text-xs text-gray-500 ml-auto">
                        Live from {fmtDate(detail.rolled_out_on)}
                        {detail.superseded_on ? ` to ${fmtDate(detail.superseded_on)}` : ""}
                    </span>
                </div>
                <div className="text-[11px] uppercase tracking-wider text-amber-700">
                    Read-only — this is how the SOP read in version {detail.version_number}.
                </div>

                <section className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div className="col-span-2"><span className="text-gray-500">SOP name: </span>{h.process_name}</div>
                    <div className="col-span-2"><span className="text-gray-500">Description: </span>{h.description || "—"}</div>
                    <div><span className="text-gray-500">Owner: </span>{h.owner_authority_name || "—"}</div>
                    <div><span className="text-gray-500">Effective date: </span>{h.effective_date ? fmtDate(h.effective_date) : "—"}</div>
                    <div><span className="text-gray-500">Review cycle: </span>{h.review_cycle_months ?? "—"} months</div>
                    {detail.change_summary && (
                        <div className="col-span-2"><span className="text-gray-500">Reason for this version: </span>{detail.change_summary}</div>
                    )}
                </section>

                <section>
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                        Steps ({detail.snapshot.steps.length})
                    </div>
                    {detail.snapshot.steps.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No steps in this version.</p>
                    ) : (
                        <ol className="flex flex-col gap-2">
                            {detail.snapshot.steps.map((s, i) => (
                                <li key={i} className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">STEP {s.step_number}</span>
                                        <span className="text-sm font-semibold text-gray-800">{s.step_name}</span>
                                        {s.is_active === 0 && <span className="status-badge sm">Inactive</span>}
                                    </div>
                                    {s.timeline_days != null && (
                                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-1">
                                            <Clock size={11} /> +{s.timeline_days} days
                                            {s.after_step_name && <span className="text-gray-400">· after "{s.after_step_name}"</span>}
                                        </div>
                                    )}
                                    {s.description && <div className="text-xs text-gray-600 mt-1 whitespace-pre-line">{s.description}</div>}
                                    {s.authorities.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 pt-1">
                                            {s.authorities.map((a, j) => (
                                                <span key={j} className="text-[11px] px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-700">
                                                    {a.authority_name} — {a.role.toLowerCase()}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ol>
                    )}
                </section>

                {detail.version_number > 1 && (
                    <section>
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                            Changes from version {detail.version_number - 1}
                        </div>
                        <SopChangesList
                            changes={detail.changes}
                            emptyText={detail.changes === null ? "The previous version was not recorded." : "No changes."}
                        />
                    </section>
                )}
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <NoDataFound
                title="No Versions Yet"
                message="A SOP gets its first version when it is approved. Each approved draft adds the next one."
            />
        );
    }

    return (
        <div className="table-list-scroll" data-testid="TBL_sop_versions">
            <table className="table-list-table">
                <thead>
                    <tr>
                        <th className="table-list-th">Version</th>
                        <th className="table-list-th">Status</th>
                        <th className="table-list-th">Live from</th>
                        <th className="table-list-th">Approved by</th>
                        <th className="table-list-th">Reason for change</th>
                        <th className="table-list-th table-list-th--right">Steps</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr
                            key={r.id}
                            className="table-list-row"
                            style={{ cursor: "pointer" }}
                            onClick={() => openVersion(r.version_number)}
                            data-testid={`ROW_sop_version_${r.version_number}`}
                        >
                            <td className="table-list-cell"><span className="table-list-cell-title">v{r.version_number}</span></td>
                            <td className="table-list-cell table-list-cell--status">
                                <span className={`status-badge sm ${r.is_live ? "success" : "neutral"}`}>
                                    {r.is_live ? "Live" : "Superseded"}
                                </span>
                            </td>
                            <td className="table-list-cell">
                                {fmtDate(r.rolled_out_on)}
                                {r.superseded_on && <div className="table-list-cell-sub">until {fmtDate(r.superseded_on)}</div>}
                            </td>
                            <td className="table-list-cell">
                                {r.approved_by_name || "—"}
                                {r.drafted_by_name && <div className="table-list-cell-sub">drafted by {r.drafted_by_name}</div>}
                            </td>
                            <td className="table-list-cell table-list-cell--truncate" title={r.change_summary || ""}>
                                {r.change_summary || "—"}
                            </td>
                            <td className="table-list-cell table-list-cell--right">{r.step_count}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default SopVersionsPanel;
